// Specimen Observer — Create World domain contracts (2026-09-20 forward plan).

export const WORLD_DRAFT_SCHEMA_VERSION = '2.0.0';
export const WORLD_SNAPSHOT_SCHEMA_VERSION = '2.0.0';
export const CONDITIONAL_ANSWER_SCHEMA_VERSION = '2.0.0';
export const CHARACTER_DRAFT_SCHEMA_VERSION = '2.0.0';
export const OPERATING_SCHEDULE_SCHEMA_VERSION = '2.0.0';
export const PLOT_DEVICE_SCHEMA_VERSION = '1.0.0';
export const SUMMARY_RECORD_SCHEMA_VERSION = '1.0.0';
export const PROMPT_MANIFEST_SCHEMA_VERSION = '2.0.0';

/** @deprecated v1 location-type screen removed — free-text `ConnectedAnswers.locationType` is authoritative. */
export type LocationType = 'business' | 'school' | 'home' | 'other';

/** @deprecated v1 guided vs quick-start screen removed — always guided in v2. */
export type CreationMode = 'guided' | 'quick_start';

/** Wizard flow per notes §§5–11 — always guided; no mode or location-type screen. */
export type WizardStepId =
  | 'welcome'
  | 'location-grounding'
  | 'connected'
  | 'time-period'
  | 'operating-hours'
  | 'review'
  | 'character-population'
  | 'characters-basic'
  | 'characters-detailed'
  | 'characters-traits'
  | 'ready-to-launch'
  | 'launched'
  | 'saved-draft';

export type AnswerStatus =
  | 'unanswered'
  | 'answered'
  | 'suggested'
  | 'skipped'
  | 'stale'
  | 'confirmed';

export type AnswerSource = 'user' | 'assumption' | 'ai_suggestion' | 'fallback';

/** @deprecated Review assumptions block removed in v2 — shim for CreateWorld imports during B4. */
export interface AssumptionRecord {
  assumptionId: string;
  fieldPath: string;
  assumption: string;
  source: AnswerSource;
  contextSourceIds: string[];
  accepted: boolean;
  createdAt: string;
}

/** Statuses where a non-blank value is present and must not be AI-overwritten. */
export type FilledAnswerStatus = 'answered' | 'suggested' | 'confirmed';

export type BlankAnswerStatus = 'unanswered';

export type SkippedAnswerStatus = 'skipped';

export type ClosedBehavior = 'leave' | 'sleep' | 'observable';
export type OperatingStatus = 'open' | 'closed';
export type TimezoneStrategy = 'device' | 'explicit' | 'location';

/** ISO weekday: 1 = Monday … 7 = Sunday. */
export type IsoWeekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type QuestionControlType =
  | 'single_select'
  | 'multi_select'
  | 'short_text'
  | 'long_text'
  | 'time_range'
  | 'day_selection'
  | 'slider'
  | 'swatch'
  | 'review_card';

export interface ConditionalQuestionDef {
  questionId: string;
  schemaVersion: string;
  controlType: QuestionControlType;
  prompt: string;
  whyItMatters: string;
  dependencies: string[];
  required: boolean;
  /** When true the field may store explicit `skipped` status (notes §7 rhythm). */
  skippable?: boolean;
  validationRule?: string;
}

/** Notes §6 — name, region, language. */
export interface LocationGrounding {
  name: string;
  region: string;
  language: string;
}

/** Notes §7 — crowd radio options. */
export type CrowdType =
  | 'public_venue'
  | 'invited_private_allowed'
  | 'members_owners_workers_students_faculty'
  | 'other';

/** Notes §7 — daily rhythm options (optional — skip). */
export type RhythmType =
  | 'steady_busy_all_day'
  | 'busy_then_slow'
  | 'seasonal_or_event_driven'
  | 'family_or_friend_driven'
  | 'other';

export interface ConnectedCrowd {
  type: CrowdType;
  /** Required when type is `other`. */
  otherDescription?: string;
}

/** Explicit skip stores `skipped` status, not blank (notes §7). */
export type RhythmAnswer =
  | { skipped: true }
  | { skipped: false; type: RhythmType; otherDescription?: string };

/** Notes §7 — connected step aggregate (also mirrored in draft answers). */
export interface ConnectedAnswers {
  locationType: string;
  description: string;
  crowd: ConnectedCrowd;
  rhythm: RhythmAnswer;
}

/** Notes §8 — present + period; rationale optional; ≥1 of present/period required. */
export interface TimePeriod {
  present?: string;
  period?: string;
  rationale?: string;
}

/**
 * Notes §9 — schedule contract.
 * `hasOperatingHours === false` means live 24/7 (UI checkbox checked).
 * When 24/7, days/times/while-closed are preserved in memory but excluded from validation.
 */
export interface OperatingSchedule {
  schemaVersion: string;
  hasOperatingHours: boolean;
  timezoneStrategy: TimezoneStrategy;
  /** IANA timezone when strategy is explicit. */
  timezone?: string;
  daysOpen: IsoWeekday[];
  openTime: string;
  closeTime: string;
  /** True when closeTime is earlier on the clock than openTime (spans midnight). */
  overnight: boolean;
  closedBehavior: ClosedBehavior;
}

export interface WorldClock {
  inWorldIso: string;
  episodeNumber: number;
}

/** Notes §11 — basic identity (first name required, last initial ≤1 alpha). */
export interface CharacterBasic {
  firstName: string;
  lastInitial?: string;
}

/** Notes §11 — detailed look screen. */
export interface CharacterAppearance {
  skinColor: string;
  topStyle: string;
  topColor: string;
  bottomStyle: string;
  bottomColor: string;
  hairStyle: string;
  hairColor: string;
}

/** Notes §11 — 11 nature sliders, 0–10 default 5. */
export interface CharacterNature {
  sociability: number;
  agreeableness: number;
  emotionality: number;
  caution: number;
  discipline: number;
  dominance: number;
  empathy: number;
  curiosity: number;
  drive: number;
  sexual_drive: number;
  casual_sex_openness: number;
}

/** Notes §11 — learned traits 0–5, strength 0–10, reason ≤100 chars. */
export interface LearnedTrait {
  traitKey: string;
  strength: number;
  reason: string;
}

export type VisitWindowUnit = 'minutes' | 'hours' | 'days';

export interface CharacterVisit {
  visitId: string;
  startAt: string;
  startWindow: { n: number; unit: VisitWindowUnit };
  endAt: string;
  endWindow: { n: number; unit: VisitWindowUnit };
  reason: string;
  rolledArrivalAt?: string;
  rolledDepartureAt?: string;
}

export type CharacterDraftStatus = 'draft' | 'confirmed' | 'removed';

export interface CharacterDraft {
  draftCharacterId: string;
  schemaVersion: string;
  basic: CharacterBasic;
  /** Saved with the world. Missing on older drafts means Male. */
  sex?: 'M' | 'F';
  /** Saved with the world. Missing on older drafts means Human. */
  species?: string;
  /** Optional free text; missing means none. */
  background?: string;
  /** Saved with the world. Missing on older drafts means Adult. */
  ageRange?: string;
  appearance: CharacterAppearance | null;
  nature: CharacterNature | null;
  traits: LearnedTrait[];
  status: CharacterDraftStatus;
  source: AnswerSource;
  dependencyIds: string[];
  updatedAt: string;
  visits?: CharacterVisit[];
  visitor?: boolean;
}

/** Directional bond from one draft character toward another (review step). */
export interface CharacterBond {
  fromId: string;
  toId: string;
  kind: string;
  /** 0–10, maps to Relationship.familiarity at launch. */
  closeness: number;
}

/** @deprecated Legacy v1 shape — parse migrates to CharacterDraft. */
export interface CharacterDraftMeta {
  draftCharacterId: string;
  schemaVersion: string;
  firstName: string;
  lastInitial?: string;
  species: string;
  sex: 'M' | 'F';
  ageRange: string;
  status: CharacterDraftStatus;
  source: AnswerSource;
  updatedAt: string;
}

/** Answer record with provenance — AI must never overwrite filled values. */
export interface ConditionalAnswerBase {
  questionId: string;
  schemaVersion: string;
  source: AnswerSource | null;
  dependencies: string[];
  updatedAt: string;
  validationError?: string;
}

export interface UnansweredConditionalAnswer extends ConditionalAnswerBase {
  status: BlankAnswerStatus;
  value: null;
}

export interface SkippedConditionalAnswer extends ConditionalAnswerBase {
  status: SkippedAnswerStatus;
  value: { skipped: true };
}

export interface StaleConditionalAnswer extends ConditionalAnswerBase {
  status: 'stale';
  value: unknown;
}

export interface FilledConditionalAnswer extends ConditionalAnswerBase {
  status: FilledAnswerStatus;
  value: unknown;
  source: AnswerSource;
}

export type ConditionalAnswer =
  | UnansweredConditionalAnswer
  | SkippedConditionalAnswer
  | StaleConditionalAnswer
  | FilledConditionalAnswer;

/** Type-level: only blank or stale answers are AI-fill targets. */
export type AiFillTargetAnswer = UnansweredConditionalAnswer | StaleConditionalAnswer;

/** Type-level: patches that would overwrite a filled answer are rejected. */
export type AiFillPatch = {
  questionId: string;
  value: unknown;
  source: 'ai_suggestion';
};

export type AiFillRejectReason = 'already_filled' | 'skipped' | 'confirmed';

export interface AiFillResult {
  applied: AiFillPatch[];
  rejected: Array<{ questionId: string; reason: AiFillRejectReason }>;
}

export interface WorldDraft {
  schemaVersion: string;
  draftId: string;
  createdAt: string;
  updatedAt: string;
  wizardStep: WizardStepId;
  /** Last builder screen the user was on when Save World ran (notes §3 resume). */
  lastVisitedStep?: WizardStepId;
  location: LocationGrounding | null;
  connected: ConnectedAnswers | null;
  answers: Record<string, ConditionalAnswer>;
  timePeriod: TimePeriod | null;
  schedule: OperatingSchedule | null;
  characterDrafts: CharacterDraft[];
  characterBonds?: CharacterBond[];
  reviewAccepted: boolean;
}

export type PlotDeviceSource = 'seeded' | 'generated';
export type PlotDeviceIntensity = 'mild' | 'moderate' | 'intense';

export interface PlotDevice {
  deviceId: string;
  schemaVersion: string;
  category: string;
  title: string;
  description: string;
  trigger?: string;
  consequenceRange?: string;
  intensity: PlotDeviceIntensity;
  source: PlotDeviceSource;
  contextSourceIds?: string[];
  dependencyIds?: string[];
  safetyStatus?: 'reviewed' | 'pending';
}

export interface PlotDeviceGenerationContext {
  worldId: string;
  worldVersion: number;
  answerIds: string[];
  generationSeed: string;
  generatedAt: string;
}

export interface DailyDeviceSelection {
  schemaVersion: string;
  worldId: string;
  inWorldDate: string;
  selectionSeed: string;
  deviceIds: string[];
  selectedAt: string;
}

export interface WorldSnapshot {
  schemaVersion: string;
  worldId: string;
  version: number;
  acceptedAt: string;
  draftId: string;
  location: LocationGrounding;
  connected: ConnectedAnswers;
  answers: Record<string, FilledConditionalAnswer & { status: 'confirmed' }>;
  timePeriod: TimePeriod;
  schedule: OperatingSchedule;
  characters: CharacterDraft[];
  characterBonds?: CharacterBond[];
  devices: PlotDevice[];
  clock: WorldClock;
  operatingStatus: OperatingStatus;
  generationContext?: PlotDeviceGenerationContext;
}

export type SummaryScope = 'location' | 'character' | 'missed_activity';

export interface SummaryRecord {
  schemaVersion: string;
  summaryId: string;
  worldId: string;
  scope: SummaryScope;
  characterId?: string;
  eventIds: string[];
  observedFacts: string;
  directorInterpretation?: string;
  priorSummaryHash?: string;
  dependencyIds?: string[];
  createdAt: string;
}

export interface HistoryEventRecord {
  eventId: string;
  worldId: string;
  occurredAt: string;
  summary: string;
  actorIds: string[];
  deviceIds: string[];
  observed: boolean;
}

/** Notes §13 — prompt manifest (no secrets / raw payloads). */
export interface PromptManifest {
  schemaVersion: string;
  manifestId: string;
  purpose: string;
  provider: 'deepseek' | 'gemini' | 'fallback';
  fallback: boolean;
  revision: number;
  answerIds: string[];
  actorIds: string[];
  eventIds: string[];
  dependencyIds: string[];
  validationRule?: string;
  createdAt: string;
}

/** Fields that must never appear in persisted domain records. */
export const FORBIDDEN_PERSISTENCE_KEYS = [
  'apiKey',
  'api_key',
  'deepseekKey',
  'deepseekApiKey',
  'geminiKey',
  'geminiApiKey',
  'secureStore',
  'rawProviderPayload',
  'rawPrompt',
  'providerSecret',
] as const;

/** Notes §9 — UI checkbox "live 24/7" is checked when hasOperatingHours is false. */
export function isLive247(schedule: OperatingSchedule): boolean {
  return !schedule.hasOperatingHours;
}

/** True when trimmed value is non-empty (AI-fill eligibility helper). */
export function isNonBlankValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'object' && value !== null && 'skipped' in value) {
    const rhythm = value as { skipped: boolean; type?: unknown };
    if (rhythm.skipped === true) return false;
    return rhythm.type != null;
  }
  return true;
}

/** Whether an answer holds a user-visible filled value (not blank/unanswered). */
export function isFilledAnswer(answer: ConditionalAnswer): answer is FilledConditionalAnswer {
  return (
    answer.status === 'answered' ||
    answer.status === 'suggested' ||
    answer.status === 'confirmed'
  );
}

/** Whether AI-fill may target this answer (blank or stale only; never skipped/filled). */
export function isAiFillTarget(answer: ConditionalAnswer): answer is AiFillTargetAnswer {
  return answer.status === 'unanswered' || answer.status === 'stale';
}

/** Whether AI-fill must reject overwriting this answer. */
export function rejectsAiFillOverwrite(answer: ConditionalAnswer): boolean {
  if (answer.status === 'skipped') return true;
  if (answer.status === 'confirmed') return true;
  if (isFilledAnswer(answer) && isNonBlankValue(answer.value)) return true;
  return false;
}

function rejectReasonFor(answer: ConditionalAnswer): AiFillRejectReason {
  if (answer.status === 'skipped') return 'skipped';
  if (answer.status === 'confirmed') return 'confirmed';
  return 'already_filled';
}

/** Apply AI-fill patches — fills ONLY blanks; never overwrites filled or skipped answers. */
export function applyAiFillPatches(
  draft: WorldDraft,
  patches: AiFillPatch[],
  now = new Date().toISOString(),
): { draft: WorldDraft; result: AiFillResult } {
  const applied: AiFillPatch[] = [];
  const rejected: AiFillResult['rejected'] = [];
  const answers = { ...draft.answers };

  for (const patch of patches) {
    const existing = answers[patch.questionId];
    if (existing && rejectsAiFillOverwrite(existing)) {
      rejected.push({ questionId: patch.questionId, reason: rejectReasonFor(existing) });
      continue;
    }
    if (!isNonBlankValue(patch.value)) {
      rejected.push({ questionId: patch.questionId, reason: 'already_filled' });
      continue;
    }

    const next: FilledConditionalAnswer = {
      questionId: patch.questionId,
      schemaVersion: existing?.schemaVersion ?? CONDITIONAL_ANSWER_SCHEMA_VERSION,
      value: patch.value,
      status: 'suggested',
      source: patch.source,
      dependencies: existing?.dependencies ?? [],
      updatedAt: now,
    };
    answers[patch.questionId] = next;
    applied.push(patch);
  }

  return {
    draft: { ...draft, answers, updatedAt: now },
    result: { applied, rejected },
  };
}
